import * as React from "react";

import { cn } from "@/lib/utils";

const Chevron = ({ path }: { path: string }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
  >
    <path d={path} />
  </svg>
);

const DOWN = "M6 9l6 6 6-6";
const formatNumber = (value: number) => value.toLocaleString("pt-BR");

export interface LoadMoreProps extends React.HTMLAttributes<HTMLDivElement> {
  shown: number;
  total: number;
  unit?: string;
  onLoadMore: () => void;
  loading?: boolean;
  done?: boolean;
  showAction?: boolean;
  loadMoreLabel?: string;
  loadingLabel?: string;
  doneLabel?: string;
  loadingIndicator?: React.ReactNode;
  progressLabel?: string;
}

/** Low-density pagination from the TB Design System. */
export const LoadMore = React.forwardRef<HTMLDivElement, LoadMoreProps>(
  (
    {
      className,
      shown,
      total,
      unit = "",
      onLoadMore,
      loading = false,
      done,
      showAction = true,
      loadMoreLabel = "Carregar mais",
      loadingLabel = "Carregando…",
      doneLabel = "Tudo carregado",
      loadingIndicator,
      progressLabel = "Progresso",
      ...props
    },
    ref,
  ) => {
    const percentage = total > 0 ? Math.round((shown / total) * 100) : 0;
    const complete = done ?? shown >= total;

    return (
      <div
        ref={ref}
        className={cn("flex w-full flex-col items-center gap-3.5", className)}
        {...props}
      >
        <span className="text-center text-[13px] tabular-nums text-[var(--fg-muted)]">
          Exibindo <b className="font-bold text-[var(--fg)]">{formatNumber(shown)}</b>{" "}
          de <b className="font-bold text-[var(--fg)]">{formatNumber(total)}</b>{" "}
          {unit}
        </span>
        <span
          aria-label={progressLabel}
          aria-valuemax={total}
          aria-valuemin={0}
          aria-valuenow={shown}
          className="h-1.5 w-[280px] max-w-full overflow-hidden rounded-full bg-[var(--tb-oxide-tint)]"
          role="progressbar"
        >
          <span
            className="block h-full rounded-full bg-tb-oxide transition-[width] duration-300"
            style={{ width: `${percentage}%` }}
          />
        </span>
        {showAction && (
          <button
            type="button"
            onClick={onLoadMore}
            disabled={complete || loading}
            className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-[var(--tb-radius-sm)] border border-[var(--tb-border-strong)] bg-tb-white px-5 text-[13px] text-[var(--fg)] transition-colors hover:enabled:bg-[var(--interaction-hover)] disabled:cursor-default disabled:opacity-40 [&_svg]:h-[15px] [&_svg]:w-[15px]"
          >
            {loading ? (
              <>
                {loadingIndicator ?? (
                  <span className="h-[15px] w-[15px] animate-spin rounded-full border-2 border-[var(--tb-oxide-tint)] border-t-tb-oxide" />
                )}
                {loadingLabel}
              </>
            ) : complete ? (
              doneLabel
            ) : (
              <>
                <Chevron path={DOWN} />
                {loadMoreLabel}
              </>
            )}
          </button>
        )}
      </div>
    );
  },
);
LoadMore.displayName = "LoadMore";
